import { utcToZonedTime } from 'date-fns-tz'

import {
  DiningOptionBehavior,
  LoyaltyBanner,
  Maybe,
  RankedPromoOfferDiscount
} from 'src/apollo/onlineOrdering'
import {
  Menus,
  MenuItem
} from 'src/public/components/online_ordering/useOOMenu'

import { DiningOptions } from 'shared/components/common/restaurant_context/RestaurantContext'

export const getGroupNameFromGuid = (guid: string, menus: Menus) => {
  for (const menu of menus) {
    if (menu?.groups) {
      for (const group of menu.groups) {
        if (group?.guid === guid) {
          return group.name
        }
      }
    }
  }
  return null
}

export const getMenuNameFromGuid = (guid: string, menus: Menus) => {
  for (const menu of menus) {
    if (menu?.guid === guid) {
      return menu.name
    }
  }
  return null
}

export const getMenuItemFromGuid = (
  guid: string,
  menus: Menus = [],
  menuItems: MenuItem[] = []
) => {
  for (const menu of menus) {
    if (menu?.groups) {
      for (const group of menu.groups) {
        if (group?.items) {
          for (const item of group.items) {
            if (item?.guid === guid) {
              return item
            }
          }
        }
      }
    }
  }

  for (const item of menuItems) {
    if (item?.guid === guid) {
      return item
    }
  }
  return null
}

export const scrollToMenu = (guid: string, isGroup: boolean) => {
  const element = document.getElementById(
    `menu${isGroup ? 'Group' : ''}-${guid}`
  )
  if (element) {
    element.focus()
    element.scrollIntoView()
  }
}

export const getOfferPrice = (
  offer: RankedPromoOfferDiscount | undefined,
  price: number
) => {
  if (!offer) {
    return price
  }
  let discountValue: Maybe<number> | undefined
  if (offer.discount?.amountType === 'BOGO') {
    discountValue = offer.discount?.bogoAction?.discountValue
  } else {
    discountValue = offer.discount?.discountValue
  }
  if (
    offer.discount?.amountType === 'FIXED' ||
    offer.discount?.bogoAction?.amountType === 'FIXED'
  ) {
    return price - (discountValue ?? 0)
  }
  // Percent based
  return price - (discountValue ? (discountValue / 100) * price : 0)
}

export const getRestrictedDiningOption = (
  triggerDiningOptions: string[] | null | undefined,
  diningOptions?: DiningOptions
): DiningOptionBehavior | undefined => {
  const deliveryGuid = diningOptions?.find(
    (option) => option?.behavior === DiningOptionBehavior.Delivery
  )?.guid
  const takeoutGuid = diningOptions?.find(
    (option) => option?.behavior === DiningOptionBehavior.TakeOut
  )?.guid
  if (!deliveryGuid || !takeoutGuid) {
    return undefined
  }
  if (
    triggerDiningOptions?.includes(deliveryGuid) &&
    !triggerDiningOptions?.includes(takeoutGuid)
  ) {
    return DiningOptionBehavior.Delivery
  } else if (
    triggerDiningOptions?.includes(takeoutGuid) &&
    !triggerDiningOptions?.includes(deliveryGuid)
  ) {
    return DiningOptionBehavior.TakeOut
  }
  return undefined
}

export const getTargetSegmentName = (
  appliedPromoCode: string | null,
  rankedPromoOfferDiscounts: RankedPromoOfferDiscount[] | undefined
): string | null =>
  rankedPromoOfferDiscounts?.find(
    (offer) => offer.promoCode.toUpperCase() === appliedPromoCode?.toUpperCase()
  )?.targetSegmentV2?.name ?? null

export const getTargetSegmentNames = (
  offers: RankedPromoOfferDiscount[] | undefined
): string[] => {
  const names: string[] = []
  for (const offer of offers ?? []) {
    const name = offer.targetSegmentV2?.name
    if (name) {
      names.push(name)
    }
  }
  return names
}

export const isLoyaltyBannerAvailable = (
  loyaltyBanner: LoyaltyBanner,
  timeZoneId: string
) => {
  if (
    !loyaltyBanner.availability?.time ||
    !loyaltyBanner.availability?.time?.startTime ||
    !loyaltyBanner.availability?.time?.endTime
  ) {
    // If no time is set, the banner is available all day
    return true
  }
  const currentTime = utcToZonedTime(new Date(Date.now()), timeZoneId)
  const startTime = utcToZonedTime(
    new Date(
      `${currentTime.toLocaleDateString()} ${
        loyaltyBanner.availability?.time?.startTime
      }`
    ),
    timeZoneId
  )
  const endTime = utcToZonedTime(
    new Date(
      `${currentTime.toLocaleDateString()} ${
        loyaltyBanner.availability?.time?.endTime
      }`
    ),
    timeZoneId
  )
  return currentTime >= startTime && currentTime <= endTime
}
